// Keep in sync with Athena `isAcceptableCabinetURL` in
// js/packages/athena/src/clients/cabinetPresizedPhotos.ts.
const S3_HOST_RE = /(?:^|\.)s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
const PASSTHROUGH_HOSTS = new Set(['images.opendoor.com', 'images.simplersell.com']);
const CABINET_ASSETS_BUCKET = 'cabinet-assets';

const parseHttpsUrl = (value: string): URL | undefined => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const isCabinetPresizedUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  const parsed = parseHttpsUrl(url);
  if (!parsed) {
    return false;
  }
  if (S3_HOST_RE.test(parsed.hostname)) {
    return true;
  }
  return PASSTHROUGH_HOSTS.has(parsed.hostname) && parsed.pathname.startsWith('/pre/');
};

export const toImagesWorkerSourceUrl = (url: string): string | undefined => {
  const parsed = parseHttpsUrl(url);
  if (!parsed) {
    return undefined;
  }
  if (PASSTHROUGH_HOSTS.has(parsed.hostname) && parsed.pathname.startsWith('/source/s3/')) {
    return url;
  }
  if (!S3_HOST_RE.test(parsed.hostname)) {
    return undefined;
  }

  const virtualHostedMarker = parsed.hostname.indexOf('.s3');
  const bucket =
    virtualHostedMarker > 0
      ? parsed.hostname.slice(0, virtualHostedMarker)
      : parsed.pathname.split('/').filter(Boolean)[0];
  if (bucket !== CABINET_ASSETS_BUCKET) {
    return undefined;
  }

  if (virtualHostedMarker > 0) {
    if (parsed.pathname === '/' || parsed.pathname === '') {
      return undefined;
    }
    return `https://images.opendoor.com/source/s3/${bucket}${parsed.pathname}`;
  }

  const [, ...keyParts] = parsed.pathname.split('/').filter(Boolean);
  if (keyParts.length === 0) {
    return undefined;
  }
  return `https://images.opendoor.com/source/s3/${bucket}/${keyParts.join('/')}`;
};

/**
 * Merges query params onto an image URL while preserving any existing query
 * string and hash fragment. Shared canonical helper so callers don't blindly
 * append `?${params}` and clobber pre-existing params/anchors.
 */
export const withImageParams = (photoUrl: string, paramsRecord: Record<string, string>): string => {
  const hashIndex = photoUrl.indexOf('#');
  const urlWithoutHash = hashIndex === -1 ? photoUrl : photoUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : photoUrl.slice(hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const currentQuery = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(currentQuery);

  Object.entries(paramsRecord).forEach(([key, value]) => {
    params.set(key, value);
  });

  const query = params.toString();
  return `${baseUrl}${query ? `?${query}` : ''}${hash}`;
};
